import * as pdfjs from 'pdfjs-dist';

// Set the worker source
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Document cache to avoid loading the same PDF multiple times
const pdfCache = new Map();

/**
 * Get a PDF document from cache or load it if not cached
 * @param {string} pdfUrl - URL to the PDF document
 * @returns {Promise<Object>} - PDF document
 */
const getPdfDocument = async (pdfUrl) => {
  // Check if document is in cache
  if (pdfCache.has(pdfUrl)) {
    return pdfCache.get(pdfUrl);
  }
  
  // Load the PDF document
  const loadingTask = pdfjs.getDocument(pdfUrl);
  const pdf = await loadingTask.promise;
  
  // Store in cache (limit cache size to 2 documents)
  if (pdfCache.size >= 2) {
    const firstKey = pdfCache.keys().next().value;
    // Clean up old PDF document to free memory
    const oldDoc = pdfCache.get(firstKey);
    if (oldDoc && typeof oldDoc.destroy === 'function') {
      oldDoc.destroy();
    }
    pdfCache.delete(firstKey);
  }
  pdfCache.set(pdfUrl, pdf);
  
  return pdf;
};

/**
 * Renders a specific region of a PDF page to a data URL
 * @param {string} pdfUrl - URL to the PDF document
 * @param {number} pageNumber - Page number to render (1-based)
 * @param {Object} boundingBox - Object with Left, Top, Width, Height in percentages (0-1)
 * @param {number} padding - Additional padding around the region in percentage of region size (0-1)
 * @returns {Promise<string>} - Data URL of the rendered image
 */
export const extractPDFRegion = async (pdfUrl, pageNumber, boundingBox, padding = 0.2) => {
  let canvas = null;
  let ctx = null;
  
  try {
    // Get the PDF document from cache or load it
    const pdf = await getPdfDocument(pdfUrl);
    
    // Get the requested page (note: PDF.js uses 0-based indexing)
    const page = await pdf.getPage(pageNumber);
    
    // Get the original viewport
    const originalViewport = page.getViewport({ scale: 1.0 });
    const { width: pageWidth, height: pageHeight } = originalViewport;
    
    // Calculate the region coordinates (convert from percentages to points)
    const regionX = boundingBox.Left * pageWidth;
    const regionY = boundingBox.Top * pageHeight;
    const regionWidth = boundingBox.Width * pageWidth;
    const regionHeight = boundingBox.Height * pageHeight;
    
    // Add padding around the region
    const paddingX = regionWidth * padding;
    const paddingY = regionHeight * padding;
    
    // Ensure the region with padding stays within the page bounds
    const x = Math.max(0, regionX - paddingX);
    const y = Math.max(0, regionY - paddingY);
    const width = Math.min(pageWidth - x, regionWidth + (paddingX * 2));
    const height = Math.min(pageHeight - y, regionHeight + (paddingY * 2));
    
    // Create a viewport that covers only the region of interest
    // Use a lower scale factor to reduce memory usage
    const scale = 3.6; // Reduced from 2.0 to save memory
    const viewport = page.getViewport({
      scale,
      // We need to offset the viewport to start at our region
      offsetX: -x * scale,
      offsetY: -y * scale
    });
    
    // Create a canvas
    canvas = document.createElement('canvas');
    canvas.width = width * scale;
    canvas.height = height * scale;
    
    ctx = canvas.getContext('2d');
    
    // Render the PDF page to the canvas
    const renderContext = {
      canvasContext: ctx,
      viewport
    };
    
    await page.render(renderContext).promise;
    
    // Highlight the actual region within the padded area
    // Center region (without padding) would be at:
    const innerX = paddingX * scale;
    const innerY = paddingY * scale;
    const innerWidth = regionWidth * scale;
    const innerHeight = regionHeight * scale;
    
    // Draw a semi-transparent highlight rectangle
    ctx.strokeStyle = 'rgba(255, 0, 0, 0.8)';
    ctx.lineWidth = 2;
    ctx.strokeRect(innerX, innerY, innerWidth, innerHeight);
    
    // Convert the canvas to a data URL - use JPEG instead of PNG for better memory efficiency
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    
    // Clean up page resources when done
    page.cleanup();
    
    return dataUrl;
  } catch (error) {
    console.error('Error extracting PDF region:', error);
    return null;
  } finally {
    // Clean up canvas to help garbage collection
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = 0;
      canvas.height = 0;
    }
  }
};

/**
 * Clear the PDF document cache to free memory
 * Should be called when no longer using PDFs
 */
export const clearPdfCache = () => {
  for (const pdf of pdfCache.values()) {
    if (pdf && typeof pdf.destroy === 'function') {
      pdf.destroy();
    }
  }
  pdfCache.clear();
};

export default {
  extractPDFRegion,
  clearPdfCache
};